const LINKEDIN_PROFILE_BASE_URL = 'https://www.linkedin.com/in/';
const LINKEDIN_PUBLIC_ID_PATTERN = /^[A-Za-z0-9%._~-]+$/;

function hostMatches(hostname: string, domain: string): boolean {
  const host = hostname.toLowerCase().replace(/^www\./, '');
  return host === domain || host.endsWith(`.${domain}`);
}

function parseUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

export function isValidLinkedInPublicId(publicId: string): boolean {
  return LINKEDIN_PUBLIC_ID_PATTERN.test(publicId);
}

export function extractLinkedInPublicId(url: string): string | null {
  const parsed = parseUrl(url);
  if (!parsed || !hostMatches(parsed.hostname, 'linkedin.com')) {
    return null;
  }
  const [section, publicId] = parsed.pathname.split('/').filter(Boolean);
  if (section !== 'in' || !publicId || !isValidLinkedInPublicId(publicId)) {
    return null;
  }
  return publicId;
}

export function resolveCommenterProfileUrl(rawUrl: string | null | undefined): string | null {
  const url = rawUrl?.trim();
  if (!url) {
    return null;
  }
  const parsed = parseUrl(url);
  if (!parsed) {
    return null;
  }
  if (hostMatches(parsed.hostname, 'instagram.com')) {
    return url;
  }
  const publicId = extractLinkedInPublicId(url);
  return publicId ? buildLinkedInProfileUrl(publicId) : null;
}

export interface FindLeadResult {
  leadId: string | null;
  assignedToOther?: boolean;
}

export type CommenterEnrichmentAction =
  | { kind: 'navigate'; leadId: string }
  | { kind: 'blocked' }
  | { kind: 'enrich'; platform: 'LinkedIn' | 'Instagram' };

export function buildLinkedInProfileUrl(publicId: string): string {
  return `${LINKEDIN_PROFILE_BASE_URL}${publicId.trim()}`;
}

export function detectCommenterPlatform(profileUrl: string): 'LinkedIn' | 'Instagram' {
  return profileUrl.includes('instagram.com') ? 'Instagram' : 'LinkedIn';
}

export function resolveCommenterEnrichmentAction(
  found: FindLeadResult,
  profileUrl: string,
): CommenterEnrichmentAction {
  if (found.leadId) {
    return { kind: 'navigate', leadId: found.leadId };
  }
  if (found.assignedToOther) {
    return { kind: 'blocked' };
  }
  return { kind: 'enrich', platform: detectCommenterPlatform(profileUrl) };
}

export type CommenterDeepLinkParam = string | string[] | undefined;

export type CommenterDeepLink =
  | { kind: 'enrich'; profileUrl: string }
  | { kind: 'missing-id' }
  | { kind: 'none' };

export function firstParam(value: CommenterDeepLinkParam): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

export function resolveCommenterDeepLink(params: {
  source: CommenterDeepLinkParam;
  publicId: CommenterDeepLinkParam;
}): CommenterDeepLink {
  const source = firstParam(params.source)?.trim().toLowerCase();
  if (source !== 'linkedin') {
    return { kind: 'none' };
  }

  const publicId = firstParam(params.publicId)?.trim();
  if (!publicId || !isValidLinkedInPublicId(publicId)) {
    return { kind: 'missing-id' };
  }

  return { kind: 'enrich', profileUrl: buildLinkedInProfileUrl(publicId) };
}
